import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";

import { ConnectionState } from "lib/consts/ARC/connection";
import { useARC } from "providers/ARC/ARCProvider";
import { useMap } from "providers/navigation/MapProvider";
import { MissionExecutionContext } from "providers/navigation/MissionExecutionProvider";
import { getActionCallerBySocket } from "lib/utils/connection";
import { ActionHandle } from "arcjs/action/base";
import { attachRosTopicWithRetry } from "lib/ros/rosTopicSubscribeWithRetry";

const NAVIGATE_ACTION_NAME = "/navigate_to_pose";
const NAVIGATE_STAGE_TOPIC = "/navigate_to_pose_stage";
const CANCEL_GOAL_SERVICE = `${NAVIGATE_ACTION_NAME}/_action/cancel_goal`;

/** Cancel all goals on the navigate_to_pose action server (ROS 2 policy: zero UUID + zero stamp). */
const cancelAllNavigateGoalsRequest = () => ({
    goal_info: {
        goal_id: { uuid: new Array(16).fill(0) },
        stamp: { sec: 0, nanosec: 0 },
    },
});

export type MapNavigatePoint = {
    x: number;
    y: number;
    theta?: number;
};

interface NavigateToPoseContextType {
    /** True while a map-initiated navigate_to_pose is in flight (until result/cancel). */
    mapNavigationActive: boolean;
    /** True when mission executor reports a running NavigateTo step. */
    missionNavigateToActive: boolean;
    /** Show top banner: map or mission Nav2 navigation. */
    showNavigateToPoseBanner: boolean;
    /** Stage key from `/navigate_to_pose_stage` (e.g. following, recovery_backup). */
    navigationStage: string;
    /** Remaining path distance in meters from action feedback, if known. */
    distanceRemaining: number | null;
    startNavigateToPoseFromMap: (point: MapNavigatePoint) => void;
    cancelNavigateToPose: () => void;
}

const NavigateToPoseContext = createContext<NavigateToPoseContextType | undefined>(undefined);
NavigateToPoseContext.displayName = "NavigateToPose";

export const useNavigateToPose = (): NavigateToPoseContextType => {
    const ctx = useContext(NavigateToPoseContext);
    if (!ctx) {
        throw new Error("useNavigateToPose must be used within NavigateToPoseProvider");
    }
    return ctx;
};

interface NavigateToPoseProviderProps {
    children: ReactNode;
}

const parseStageMessage = (message: unknown): string => {
    if (typeof message === "string") return message;
    const data = (message as { data?: unknown })?.data;
    return typeof data === "string" ? data : "";
};

const parseDistanceRemaining = (feedback: unknown): number | null => {
    const raw =
        (feedback as { distance_remaining?: unknown })?.distance_remaining ??
        (feedback as { feedback?: { distance_remaining?: unknown } })?.feedback
            ?.distance_remaining;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
};

const NavigateToPoseProvider = ({ children }: NavigateToPoseProviderProps) => {
    const { controlSocket, connectionState, callService } = useARC();
    const { setNavigateGoalActive } = useMap();
    const actionCaller = useMemo(
        () => getActionCallerBySocket(controlSocket),
        [controlSocket],
    );
    const missionExec = useContext(MissionExecutionContext);

    const [mapNavigationActive, setMapNavigationActive] = useState(false);
    const [navigationStage, setNavigationStage] = useState("");
    const [distanceRemaining, setDistanceRemaining] = useState<number | null>(null);
    const mapHandleRef = useRef<ActionHandle | null>(null);
    /** Bumped on each new map goal / cancel so stale preempted-goal callbacks cannot hide the banner. */
    const mapGoalGenerationRef = useRef(0);

    const missionNavigateToActive = useMemo(() => {
        if (!missionExec?.isExecuting) return false;

        const fb = missionExec.lastFeedback;
        if (fb?.action_type === "NavigateTo" && fb.status === "running") {
            return true;
        }

        const steps = missionExec.executorState?.step_statuses ?? [];
        const running = steps.find((s) => s.status === "running");
        return running?.action_type === "NavigateTo";
    }, [
        missionExec?.isExecuting,
        missionExec?.lastFeedback,
        missionExec?.executorState?.step_statuses,
    ]);

    const showNavigateToPoseBanner = mapNavigationActive || missionNavigateToActive;

    const resetMapNavigation = useCallback(() => {
        console.log("[NavigateToPoseProvider] resetMapNavigation -> mapNavigationActive=false");
        setMapNavigationActive(false);
        mapHandleRef.current = null;
        setDistanceRemaining(null);
    }, []);

    const cancelNavigateToPose = useCallback(() => {
        console.log("[NavigateToPoseProvider] cancelNavigateToPose called", {
            connectionState,
            cancelService: CANCEL_GOAL_SERVICE,
            showNavigateToPoseBanner,
            mapNavigationActive,
            missionNavigateToActive,
            hasMapHandle: !!mapHandleRef.current,
        });

        // Invalidate any in-flight goal callbacks before cancel completes.
        mapGoalGenerationRef.current += 1;

        if (!actionCaller || connectionState !== ConnectionState.Connected) {
            console.warn(
                "[NavigateToPoseProvider] Cancel skipped: control source unavailable or disconnected; resetting local map navigation state"
            );
            resetMapNavigation();
            return;
        }

        // Cancel the goal we started (if we still hold its handle) …
        void mapHandleRef.current?.cancel();

        // … and, belt-and-suspenders, cancel-all on the action server via its
        // cancel_goal service (covers goals started elsewhere, e.g. a mission).
        if (typeof callService === "function") {
            void callService(CANCEL_GOAL_SERVICE, cancelAllNavigateGoalsRequest()).catch(
                (err) => {
                    console.warn(
                        "[NavigateToPoseProvider] cancel_goal service failed",
                        err
                    );
                }
            );
        }

        resetMapNavigation();
    }, [
        actionCaller,
        connectionState,
        resetMapNavigation,
        showNavigateToPoseBanner,
        mapNavigationActive,
        missionNavigateToActive,
        callService,
    ]);

    const startNavigateToPoseFromMap = useCallback(
        (point: MapNavigatePoint) => {
            console.log("[NavigateToPoseProvider] startNavigateToPoseFromMap called", {
                point,
                hasActionCaller: !!actionCaller,
                connectionState,
            });

            if (!actionCaller || connectionState !== ConnectionState.Connected) {
                console.warn("[NavigateToPoseProvider] Cannot navigate: control source not connected");
                return;
            }

            const theta = Number.isFinite(point.theta) ? point.theta : 0;
            const halfTheta = theta / 2;
            const goalMessage = {
                pose: {
                    header: {
                        stamp: { sec: 0, nanosec: 0 },
                        frame_id: "map",
                    },
                    pose: {
                        position: { x: point.x, y: point.y, z: 0 },
                        orientation: {
                            x: 0,
                            y: 0,
                            z: Math.sin(halfTheta),
                            w: Math.cos(halfTheta),
                        },
                    },
                },
            };

            // New goal preempts the previous one on the server; ignore the old goal's
            // canceled/result callbacks so the Cancel banner stays visible.
            const generation = ++mapGoalGenerationRef.current;

            console.log("[NavigateToPoseProvider] Sending navigate_to_pose goal", {
                action: NAVIGATE_ACTION_NAME,
                goalMessage,
                generation,
            });
            setMapNavigationActive(true);
            setDistanceRemaining(null);
            setNavigationStage("navigating");

            actionCaller
                .sendGoal(NAVIGATE_ACTION_NAME, goalMessage, {
                    onResult: (result) => {
                        console.log("[NavigateToPoseProvider] navigate_to_pose result callback", {
                            result,
                            generation,
                            current: mapGoalGenerationRef.current,
                        });
                        if (generation !== mapGoalGenerationRef.current) return;
                        resetMapNavigation();
                    },
                    onFeedback: (feedback) => {
                        if (generation !== mapGoalGenerationRef.current) return;
                        const distance = parseDistanceRemaining(feedback);
                        if (distance != null) {
                            setDistanceRemaining(distance);
                        }
                    },
                    onFailed: (error) => {
                        console.warn(
                            "[NavigateToPoseProvider] navigate_to_pose failed/canceled callback",
                            { error, generation, current: mapGoalGenerationRef.current }
                        );
                        if (generation !== mapGoalGenerationRef.current) return;
                        resetMapNavigation();
                    },
                })
                .then((handle) => {
                    if (generation !== mapGoalGenerationRef.current) {
                        handle.stop();
                        return;
                    }
                    mapHandleRef.current = handle;
                })
                .catch((e) => {
                    console.error("[NavigateToPoseProvider] sendGoal failed:", e);
                    if (generation !== mapGoalGenerationRef.current) return;
                    resetMapNavigation();
                });
        },
        [actionCaller, connectionState, resetMapNavigation]
    );

    useEffect(() => {
        if (connectionState === ConnectionState.Disconnected) {
            console.warn(
                "[NavigateToPoseProvider] Connection disconnected; resetting map navigation state"
            );
            mapGoalGenerationRef.current += 1;
            resetMapNavigation();
            setNavigationStage("");
        }
    }, [connectionState, resetMapNavigation]);

    useEffect(() => {
        if (!controlSocket || connectionState !== ConnectionState.Connected) {
            return;
        }

        return attachRosTopicWithRetry(
            controlSocket,
            NAVIGATE_STAGE_TOPIC,
            (message) => {
                setNavigationStage(parseStageMessage(message));
            },
            { retryUntilFirstMessage: false }
        );
    }, [controlSocket, connectionState]);

    useEffect(() => {
        if (!showNavigateToPoseBanner) {
            setNavigationStage("");
            setDistanceRemaining(null);
        }
    }, [showNavigateToPoseBanner]);

    useEffect(() => {
        console.log("[NavigateToPoseProvider] Banner visibility changed", {
            showNavigateToPoseBanner,
            mapNavigationActive,
            missionNavigateToActive,
            navigationStage,
            distanceRemaining,
        });
    }, [
        showNavigateToPoseBanner,
        mapNavigationActive,
        missionNavigateToActive,
        navigationStage,
        distanceRemaining,
    ]);

    // Gate planned-path visibility in MapProvider while map or mission navigation is active.
    useEffect(() => {
        setNavigateGoalActive(showNavigateToPoseBanner);
    }, [showNavigateToPoseBanner, setNavigateGoalActive]);

    const value: NavigateToPoseContextType = {
        mapNavigationActive,
        missionNavigateToActive,
        showNavigateToPoseBanner,
        navigationStage,
        distanceRemaining,
        startNavigateToPoseFromMap,
        cancelNavigateToPose,
    };

    return (
        <NavigateToPoseContext.Provider value={value}>{children}</NavigateToPoseContext.Provider>
    );
};

export default NavigateToPoseProvider;
